import { useState } from "react";
import { useDispatch } from "react-redux";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { RiCloseLine } from "react-icons/ri";
import Input from "../../Utility/Input/Input";
import Typography from "../../Utility/Typography";
import Button from "../../Utility/Button/Button";
import FormSubmitButton from "../../Utility/Button/FormSubmitButton";
import Dropdown from "../../Utility/Dropdown/Dropdown";
import ErrorMsg from "../../Utility/ErrorMsg/ErrorMsg";
import { createTeam } from "../../Redux/Thunks/teamsThunk";
import { logger } from "../../utils/logger";
import { showSuccess } from "../../utils/toast";
import { getThunkRejectMessage } from "../../utils/thunkErrorMessage";
import { isGlobalApiErrorModalOpen } from "../../utils/globalApiErrorModal";
import { AddTeamSchema } from "../../FormsValidations/Validations";
import "../Clients/clients.css";

const AddTeam = ({ isOpen, onClose, onSuccess }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    reset,
    setError,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(AddTeamSchema),
    defaultValues: {
      teamName: "",
      status: "Active",
    },
  });

  // Map form status to API enum (Active → ACTIVE, Inactive → INACTIVE; legacy "In-Active" supported)
  const toStatus = (value) => {
    if (!value) return "ACTIVE";
    const map = { Active: "ACTIVE", Inactive: "INACTIVE", "In-Active": "INACTIVE" };
    return map[value] || value.toUpperCase().replace(/\s+/g, "_");
  };

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const payload = {
        name: (data.teamName || "").trim(),
        status: toStatus(data.status),
      };
      await dispatch(createTeam(payload)).unwrap();

      showSuccess("Team added successfully");
      reset();

      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (error) {
      logger.error("Error adding team:", error);
      if (isGlobalApiErrorModalOpen()) return;
      setError("submit", {
        type: "manual",
        message: getThunkRejectMessage(error, "Failed to add team. Please try again."),
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      reset();
      onClose();
    }
  };

  const handleReset = () => {
    if (!loading) {
      reset();
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Modal Overlay */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-[9999] flex items-center justify-center p-4"
        onClick={handleClose}
      >
        {/* Modal Content */}
        <div
          className="bg-surface-primary rounded-lg shadow-lg w-full max-w-md relative"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Modal Header */}
          <div className="flex items-center justify-between p-4 border-b border-border-primary">
            <Typography variant="h5" className="font-semibold text-text-primary">
              Add Team
            </Typography>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleClose}
              disabled={loading}
              className="!text-text-secondary hover:!text-text-primary transition-colors !border-0 !shadow-none !bg-transparent !p-0 !px-0 !py-0 !font-normal !inline-flex !items-center !justify-center disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Close modal"
            >
              <RiCloseLine className="w-5 h-5" />
            </Button>
          </div>

          {/* Modal Body */}
          <div className="p-4">
            <form onSubmit={handleSubmit(onSubmit)}>
              {/* Team Name Field */}
              <div className="mb-4">
                <Typography
                  variant="text2"
                  component="label"
                  htmlFor="teamName"
                  className="font-medium text-text-primary mb-1 block"
                >
                  Team Name
                  <Typography variant="span1" className="text-red-600 pl-1" component="span">
                    *
                  </Typography>
                </Typography>
                <Input
                  type="text"
                  {...register("teamName")}
                  className={`w-full border border-border-primary rounded-sm px-3 py-1.5 text-text-primary bg-surface-primary ${
                    errors.teamName ? "border-badge-danger" : ""
                  }`}
                  id="teamName"
                  placeholder="Enter team name"
                  label=""
                  error={errors.teamName?.message}
                  noWrapper={true}
                  disabled={loading}
                />
                <ErrorMsg message={errors.teamName?.message} className="mt-1" />
              </div>

              {/* Status Field */}
              <div className="mb-4">
                <Controller
                  name="status"
                  control={control}
                  render={({ field }) => (
                    <Dropdown
                      {...field}
                      label="Status"
                      options={["Active", "Inactive"]}
                      placeholder="Select Status"
                      error={errors.status?.message}
                      disabled={loading}
                      required={true}
                      searchable={false}
                      className="text-sm"
                    />
                  )}
                />
              </div>

              {/* Submit Error */}
              {errors.submit && <ErrorMsg message={errors.submit.message} className="mb-4" />}

              {/* Submit Button */}
              <div className="flex justify-end gap-2 mt-6">
                <Button
                  type="button"
                  variant="danger"
                  size="md"
                  disabled={loading}
                  onClick={handleReset}
                  className="btn-reset"
                >
                  Reset
                </Button>
                <FormSubmitButton
                  loading={loading}
                  label="Add Team"
                  loadingText="Add Team"
                  idleIconClass="ri-add-circle-line"
                />
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddTeam;
